import React, { useState, useEffect } from "react";
import { X, FilePenLine, CircleDollarSign, Tag, ReceiptText, Repeat } from 'lucide-react';

const EditTransactionModal = ({ isOpen, onClose, transaction, onSubmit }) => {
  const [formData, setFormData] = useState({
    amount: "",
    category: "",
    description: "",
    recurrence: "none",
  });
  const [loadedTransaction, setLoadedTransaction] = useState(null);

  const expenseCategories = [
    "Food", "Transport", "Utilities", "Shopping", "Entertainment", "Healthcare"
  ];
  const incomeCategories = [
    "Salary", "Freelance", "Investments", "Consulting", "Online Sales", "Gifts"
  ];

  useEffect(() => {
    if (transaction) {
      setLoadedTransaction(transaction);
      setFormData({
        amount: transaction.amount,
        category: transaction.category,
        description: transaction.description || "",
        recurrence: transaction.recurrence || "none",
      });
    }
  }, [transaction]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const originalDate = new Date(
        loadedTransaction.date.includes('/') || loadedTransaction.date.includes(' ') ? transaction.date : loadedTransaction.date
    );

    const updatedTransaction = {
      id: loadedTransaction.id,
      user: loadedTransaction.user,
      category: formData.category,
      amount: parseFloat(formData.amount),
      currency: loadedTransaction.currency,
      converted_amount: loadedTransaction.converted_amount,
      date: originalDate.toISOString(), 
      description: formData.description || loadedTransaction.description,
      recurrence: formData.recurrence || loadedTransaction.recurrence,
      type: loadedTransaction.type,
    };

    onSubmit(updatedTransaction);
  };

  if (!isOpen || !loadedTransaction) return null;

  const categories =
    loadedTransaction.type === "income" ? incomeCategories : expenseCategories;

  const typeStyles = {
    income: "bg-green-100 text-green-800",
    expense: "bg-red-100 text-red-800",
  };
  
  const selectArrowStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.75rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 backdrop-blur-sm p-4">
      {/* Added animation class */}
      <div className="bg-slate-50 p-6 rounded-xl w-full max-w-md shadow-2xl animate-scale-in">
        {/* --- Header with Icon and Close Button --- */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <FilePenLine className="text-[#2ecfe3]" size={24} />
            <h2 className="text-xl font-bold text-gray-800">Edit Transaction</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* --- Context Badge --- */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${typeStyles[loadedTransaction.type]}`}>
                {loadedTransaction.type.charAt(0).toUpperCase() + loadedTransaction.type.slice(1)}
            </span>
            <span className="text-sm text-gray-500">
                ID: {loadedTransaction.id.slice(0, 8)}...
            </span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* --- Input Field with Icon: Amount --- */}
          <div>
            <label htmlFor="amount" className="text-sm font-medium text-gray-600 mb-1 block">Amount</label>
            <div className="relative">
                <CircleDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                    id="amount"
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full bg-white border border-slate-200 pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2ecfe3] focus:border-[#2ecfe3] transition"
                    required
                    step="0.01"
                />
            </div>
          </div>

          {/* --- Select Field with Icon: Category --- */}
          <div>
            <label htmlFor="category" className="text-sm font-medium text-gray-600 mb-1 block">Category</label>
            <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-white border border-slate-200 pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2ecfe3] focus:border-[#2ecfe3] transition appearance-none"
                    style={selectArrowStyle}
                    required
                >
                    {categories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                    ))}
                </select>
            </div>
          </div>
          
          {/* --- Input Field with Icon: Description --- */}
          <div>
            <label htmlFor="description" className="text-sm font-medium text-gray-600 mb-1 block">Description</label>
            <div className="relative">
                <ReceiptText className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                    id="description"
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full bg-white border border-slate-200 pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2ecfe3] focus:border-[#2ecfe3] transition"
                />
            </div>
          </div>

          {/* --- Select Field with Icon: Recurrence --- */}
          <div>
            <label htmlFor="recurrence" className="text-sm font-medium text-gray-600 mb-1 block">Recurrence</label>
            <div className="relative">
                <Repeat className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <select
                    id="recurrence"
                    name="recurrence"
                    value={formData.recurrence}
                    onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                    className="w-full bg-white border border-slate-200 pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2ecfe3] focus:border-[#2ecfe3] transition appearance-none"
                    style={selectArrowStyle}
                    required
                >
                    <option value="none">None</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                </select>
            </div>
          </div>

          {/* --- Modernized Action Buttons --- */}
          <div className="mt-8 flex justify-end gap-4 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 rounded-lg text-sm font-semibold text-gray-600 bg-transparent hover:bg-gray-100 border border-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 rounded-lg text-sm font-semibold text-white bg-[#2ecfe3] hover:bg-[#28bfcf] transition-all shadow-sm hover:shadow-md"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTransactionModal;
